import type { UseQueryResult } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import React, { useState } from 'react';

import { Badge } from '@dashboard/components/ui/badge';
import { Button } from '@dashboard/components/ui/button';
import { Checkbox } from '@dashboard/components/ui/checkbox';
import { Input } from '@dashboard/components/ui/input';
import { Label } from '@dashboard/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import {
  type LiveEmail,
  type LivePage,
  type PageSummary,
  useAdminEmailTemplateLiveEmail,
  useAdminEmailTemplateLivePage,
  useAdminEmailTemplatePages,
} from '@dashboard/hooks/queries/useAdminEmailTemplates';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import { isDevelopment } from '@dashboard/services/env';

import {
  Columns,
  ListButton,
  ListHeading,
  ListSkeleton,
  SidebarHeader,
  SidebarList,
  StagePlaceholder,
  StageSkeleton,
} from './EmailTemplatesLayout';
import PreviewStage, { errorDetails } from './PreviewStage';
import useEmailTemplatesUrl from './useEmailTemplatesUrl';

// Radix Select cannot hold an empty value, so the page's own default price needs a stand-in.
const DEFAULT_PRICE = 'default';

function PageResults({
  pages,
  search,
  onSelect,
}: {
  pages: UseQueryResult<PageSummary[]>;
  search: string;
  onSelect: (pageId: string) => void;
}) {
  if (pages.isLoading) return <ListSkeleton />;
  if (pages.error) return null;

  const results = pages.data ?? [];

  return (
    <SidebarList label="Pages">
      <ListHeading>
        {search ? 'Matching pages' : 'Newest pages'} ({results.length})
      </ListHeading>
      {results.length === 0 && (
        <p className="p-2 text-sm text-muted-foreground">
          {search ? `No pages match "${search}". Try a page ID or seller ID.` : 'No pages yet.'}
        </p>
      )}
      {results.map(page => (
        <ListButton
          key={page.id}
          active={false}
          onClick={() => onSelect(page.id)}
          aside={
            <Badge variant="outline" className="capitalize">
              {page.type}
            </Badge>
          }
        >
          <span className="block truncate font-medium">{page.name}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {page.seller ? page.seller.name : 'No seller'}
          </span>
        </ListButton>
      ))}
    </SidebarList>
  );
}

function PageDetail({
  livePage,
  activeEmailKey,
}: {
  livePage: LivePage;
  activeEmailKey: string | undefined;
}) {
  const url = useEmailTemplatesUrl();
  const { page, prices, emails, charges } = livePage;

  return (
    <SidebarList label="Page emails">
      <div className="space-y-1 px-2 pt-2">
        <p className="font-medium break-words">{page.name}</p>
        <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{page.seller ? page.seller.name : 'No seller'}</span>
          <Badge variant="outline" className="capitalize">
            {page.type}
          </Badge>
        </p>
      </div>

      {prices.length > 0 && (
        <div className="space-y-1.5 px-2 pt-4">
          <Label htmlFor="email-templates-price">Price</Label>
          <Select
            value={url.price || DEFAULT_PRICE}
            onValueChange={value => url.update({ price: value === DEFAULT_PRICE ? '' : value })}
          >
            <SelectTrigger id="email-templates-price" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DEFAULT_PRICE}>Default price</SelectItem>
              {prices.map(price => (
                <SelectItem key={price.id} value={price.id}>
                  {price.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <ListHeading>Emails</ListHeading>
      {emails.map(email => (
        <ListButton
          key={email.key}
          active={!url.charge && email.key === activeEmailKey}
          onClick={() => url.update({ email: email.key, charge: '' })}
        >
          <span className="block truncate">{email.label}</span>
        </ListButton>
      ))}

      {(charges.length > 0 || page.type === 'checkout') && (
        <>
          <ListHeading>Recent orders</ListHeading>
          {charges.length === 0 && (
            <p className="px-2 text-sm text-muted-foreground">
              No orders yet, so the confirmation uses a sample order.
            </p>
          )}
          {charges.map(charge => (
            <ListButton
              key={charge.id}
              active={url.charge === charge.id}
              onClick={() => url.update({ charge: charge.id, email: charge.emails[0]?.key ?? '' })}
              aside={charge.livemode === false && <Badge variant="secondary">Test</Badge>}
            >
              <span className="flex items-baseline gap-2">
                <span className="font-medium">#{charge.orderId ?? '?'}</span>
                <span className="tabular-nums">{charge.amount}</span>
                {charge.status && (
                  <span className="text-xs text-muted-foreground capitalize">
                    {charge.status.replace(/_/g, ' ')}
                  </span>
                )}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {charge.customer ?? 'No customer'}
              </span>
            </ListButton>
          ))}
        </>
      )}
    </SidebarList>
  );
}

export default function SellerPagesPanel() {
  const url = useEmailTemplatesUrl();
  const [includeTests, setIncludeTests] = useState(false);
  const { search, searchInput, setSearchInput } = useSearchInput(url.q);

  const pages = useAdminEmailTemplatePages(search, includeTests, { enabled: !url.page });
  const live = useAdminEmailTemplateLivePage(url.page, url.price);

  const charge = url.charge ? live.data?.charges.find(item => item.id === url.charge) : undefined;
  const emails: LiveEmail[] = (url.charge ? charge?.emails : live.data?.emails) ?? [];
  const email = emails.find(item => item.key === url.email) ?? emails[0];

  const liveEmail = useAdminEmailTemplateLiveEmail(email?.path);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    url.update({ q: value.trim() });
  };

  const sidebar = url.page ? (
    <>
      <SidebarHeader>
        <Button
          variant="ghost"
          size="sm"
          className="-ml-1"
          onClick={() => url.update({ page: '', price: '', email: '', charge: '' })}
        >
          <ChevronLeft />
          All pages
        </Button>
      </SidebarHeader>
      {live.isLoading ? (
        <ListSkeleton />
      ) : (
        live.data && <PageDetail livePage={live.data} activeEmailKey={email?.key} />
      )}
    </>
  ) : (
    <>
      <SidebarHeader>
        <Input
          type="search"
          aria-label="Search pages"
          placeholder="Page ID, seller ID, page or seller name"
          autoComplete="off"
          spellCheck={false}
          value={searchInput}
          onChange={event => handleSearchChange(event.target.value)}
        />
        {pages.error && (
          <p role="alert" className="text-sm text-destructive">
            {errorDetails(pages.error).message}
          </p>
        )}
        {isDevelopment && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="email-templates-include-tests"
              checked={includeTests}
              onCheckedChange={checked => setIncludeTests(checked === true)}
            />
            <Label htmlFor="email-templates-include-tests" className="font-normal">
              Include e2e pages
            </Label>
          </div>
        )}
      </SidebarHeader>
      <PageResults
        pages={pages}
        search={search}
        onSelect={pageId => url.update({ page: pageId, price: '', email: '', charge: '' })}
      />
    </>
  );

  const stage = (() => {
    if (!url.page) {
      return (
        <StagePlaceholder title="Pick a page">
          Search for a page on the left to preview its emails with its real data.
        </StagePlaceholder>
      );
    }
    if (live.error) {
      return (
        <StagePlaceholder title="Could not load that page">
          {errorDetails(live.error).message}
        </StagePlaceholder>
      );
    }
    if (!live.data) return <StageSkeleton />;

    const { page } = live.data;
    const title = email?.label ?? page.name;

    return (
      <PreviewStage
        title={title}
        header={
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground break-words">
              {page.name}
              {page.seller && ` · ${page.seller.name}`}
              {charge && ` · Order #${charge.orderId ?? '?'}`}
            </p>
            <h2 className="text-lg leading-snug font-semibold break-words">{title}</h2>
          </div>
        }
        tabs={charge ? charge.emails.map(item => ({ value: item.key, label: item.label })) : []}
        tabsLabel="Order emails"
        activeTab={email?.key}
        onTabChange={value => url.update({ email: value })}
        html={liveEmail.data?.html}
        isLoading={liveEmail.isLoading}
        isFetching={liveEmail.isFetching}
        error={liveEmail.error}
        onReload={() => liveEmail.refetch()}
        placeholder={
          !email && (
            <StagePlaceholder title="No emails for this order">
              Pick another order.
            </StagePlaceholder>
          )
        }
      />
    );
  })();

  return <Columns sidebar={sidebar} stage={stage} />;
}
